import {AxiosError,} from 'axios'
import React, {useMemo} from 'react'
import {Button, ButtonType} from '@UI/components/controls/Button/Button.tsx'
import {ArrowTurnBack} from '@UI/components/icons/ArrowTurnBack.tsx'
import {Refresh} from '@UI/components/icons/Refresh.tsx'
import {AccessDenied} from '@UI/illustrationsSvg/AccessDenied.tsx'
import {PageNotFound} from '@UI/illustrationsSvg/PageNotFound.tsx'
import {SomeError} from '@UI/illustrationsSvg/SomeError.tsx'
import {
    Body_14_Coral_Primary_Light_14,
    Title_20_Coral_Strong_emphasis_20
} from '@UI/typography/generated/typography.tsx'
import {Gt} from '../../i18n/Gt.tsx'
import {Stack} from '../Stack/Stack.tsx'
import {Tcenter} from '../Typo/Typo.tsx'
import styles from "@src/Layers/ErrorLayer/errorLayer.module.less";

export const codeToMessageMap: Record<string, React.ReactNode> = {
    '500': <Gt>Something went wrong</Gt>,
    '403': <Gt>You don't have access</Gt>,
    '401': <Gt>You not authorized</Gt>,
    '404': <Gt>Data not found</Gt>
}

const ErrorImage = ({code, imageSize}: { code?: number; imageSize?: number }) => {
    switch (code) {
        case 404:
            return <PageNotFound style={{maxWidth: '70%'}}/>
        case 401:
            return <AccessDenied style={{maxWidth: '70%'}}/>
        default:
            return <SomeError style={{height: imageSize ?? 168, width: 'auto'}}/>
    }
}

export interface VarietyError {
    message: string,
    status: string,
}

export interface ErrorContentProps {
    close?: () => void
    loading: boolean
    handle?: () => void
    error: AxiosError | VarietyError
    imageSize?: number
    customMessage?: React.ReactNode
    center?: boolean
}

export const ErrorModalContent = function (
    {
        error,
        loading,
        handle,
        close,
        imageSize,
        customMessage,
        center = true
    }: ErrorContentProps) {
    const message = useMemo(() => {
        if (customMessage) return customMessage
        if (error instanceof AxiosError) {
            // Handle validation_error format: { type, errors: [{ code, detail, attr }] }
            const data = error.response?.data as any
            if (data?.type === 'validation_error' && Array.isArray(data?.errors) && data.errors.length > 0) {
                return (
                    <Stack direction={'column'} gap={4} alignItems={'center'}>
                        {data.errors.map((err: { detail: string; attr?: string }, i: number) => (
                            <span key={i}><Gt>{err.detail}</Gt></span>
                        ))}
                    </Stack>
                )
            }
            if (error?.status) {
                const message = codeToMessageMap[error.status.toString()]
                if (message) return message
            }
            // client_error / server_error envelope (drf-standardized-errors): show the backend details
            if (Array.isArray(data?.errors) && data.errors.length > 0) {
                return (
                    <Stack direction={'column'} gap={4} alignItems={'center'}>
                        {data.errors.map((err: { detail: string; attr?: string }, i: number) => (
                            <span key={i}><Gt prefix='ErrorMessages'>{err.detail}</Gt></span>
                        ))}
                    </Stack>
                )
            }
            return (
                <span>
                    <Gt>Error</Gt>: <Gt prefix='ErrorMessages'>{error.message}</Gt>
                </span>
            )
        } else if (error.message) {
            return (
                <Gt prefix='ErrorMessages'>{error.message}</Gt>
            )
        }
    }, [error])

    if (!error) return null
    return (
        <Stack gap={25} alignItems={'center'} grow justifyContent={center ? 'center' : undefined} direction={'column'}
               className={styles.baseErrorPopup}>
            <ErrorImage code={error.status as number} imageSize={imageSize}/>
            <div className={styles.message}>
                <Title_20_Coral_Strong_emphasis_20>
                    {message}
                </Title_20_Coral_Strong_emphasis_20>
            </div>
            <Stack gap={16} style={{flexWrap: 'wrap'}} justifyContent={'center'} alignItems={'center'}>
                {error.status !== 404 && handle && (
                    <Button disabled={loading} onClick={handle} icon={<Refresh size={22}/>} type={ButtonType.third}>
                        <Gt>Try again</Gt>
                    </Button>
                )}
                {close && (
                    <Button disabled={loading} onClick={close} icon={<ArrowTurnBack/>} type={ButtonType.primary}>
                        <Gt>Cancel</Gt>
                    </Button>
                )}
            </Stack>
        </Stack>
    )
}

export const AccessDeniedContent = () => (
    <Stack grow justifyContent='start' gap={24} alignItems={'center'} style={{padding: 16}} direction={'column'}>
        <AccessDenied style={{transform: 'scale(0.7)', flexShrink: 1, maxWidth: '100%'}}/>
        <Gt>Access denied</Gt>
        <Body_14_Coral_Primary_Light_14>
            <Tcenter>
                <Gt>No permissions</Gt>
            </Tcenter>
        </Body_14_Coral_Primary_Light_14>
    </Stack>
)
